import { Component, ErrorInfo, ReactNode } from 'react';
import { logger } from '@/shared/utils/logger';
import { ROUTES } from '@/config/routeConfig';
import { useUserOptional } from '@/contexts/UserContext';
import { getAccountDetailsPath } from '@/shared/utils/account-details-routes';
import { getCustomerDetailsPath } from '@/shared/utils/customer-details-routes';
import {
  isCustomerUserProfile,
  isPlatformUserProfile,
  isTenantUserProfile,
} from '@/shared/utils/auth-utils';

interface Props {
  children: ReactNode;
  fallback?: ReactNode;
  /** Recovery link (e.g. /home for SUPER_ADMIN, account details for others). Defaults to "/". */
  recoveryHref?: string;
  /** Label for the recovery link (e.g. "Return to Home", "Return to Account"). Defaults to "Return to Home". */
  recoveryLabel?: string;
}

interface State {
  hasError: boolean;
  error: Error | null;
}

/**
 * Catches JavaScript errors in the child component tree and renders a fallback UI
 * instead of crashing. Logs errors for debugging and optional reporting.
 * Use recoveryHref/recoveryLabel for role-appropriate recovery (e.g. Home for SUPER_ADMIN, Account for others).
 */
export class ErrorBoundary extends Component<Props, State> {
  constructor(props: Props) {
    super(props);
    this.state = { hasError: false, error: null };
  }

  static getDerivedStateFromError(error: Error): State {
    return { hasError: true, error };
  }

  componentDidCatch(error: Error, errorInfo: ErrorInfo): void {
    logger.error('ErrorBoundary caught an error', { error, componentStack: errorInfo.componentStack }, 'ErrorBoundary');
  }

  render(): ReactNode {
    if (this.state.hasError && this.state.error) {
      if (this.props.fallback) {
        return this.props.fallback;
      }
      const href = this.props.recoveryHref ?? '/';
      const label = this.props.recoveryLabel ?? 'Return to Home';
      return (
        <div className="min-h-screen flex flex-col bg-background">
          <div className="flex-1 flex items-center justify-center p-4">
            <div className="text-center max-w-md">
              <h1 className="text-2xl font-bold mb-2">Something went wrong</h1>
              <p className="mb-6">
                An unexpected error occurred. Please try refreshing the page or use the link below.
              </p>
              <a
                href={href}
                className="inline-flex items-center justify-center rounded-md text-sm font-medium bg-primary text-primary-foreground hover:bg-primary/90 h-10 px-4 py-2"
              >
                {label}
              </a>
            </div>
          </div>
        </div>
      );
    }
    return this.props.children;
  }
}

/**
 * Error boundary with recovery link derived from current user role:
 * - SUPER_ADMIN (or no accounts): link to Home (/home), "Return to Home"
 * - Other roles with selected organization: link to Organization details, "Return to Organization"
 * - Other roles without organization: link to Organizations list, "Return to Organizations"
 * - Not logged in / outside UserProvider: link to /, "Return to start"
 */
export function ErrorBoundaryWithRecovery({ children }: { children: ReactNode }) {
  const userContext = useUserOptional();

  let recoveryHref = '/';
  let recoveryLabel = 'Return to start';

  if (userContext?.isInitialized && userContext?.currentUser) {
    const { currentUser, selectedOrganization } = userContext;
    if (isPlatformUserProfile(currentUser)) {
      recoveryHref = ROUTES.ACCOUNTS;
      recoveryLabel = 'Return to Accounts';
    } else if (isCustomerUserProfile(currentUser)) {
      recoveryHref = getCustomerDetailsPath(currentUser?.name ?? '');
      recoveryLabel = 'Return to Customer';
    } else if (isTenantUserProfile(currentUser) && selectedOrganization) {
      recoveryHref = getAccountDetailsPath(selectedOrganization.organizationId);
      recoveryLabel = 'Return to Account';
    } else {
      recoveryHref = ROUTES.LOGIN
      recoveryLabel = 'Return to Login';
    }
  }

  return (
    <ErrorBoundary recoveryHref={recoveryHref} recoveryLabel={recoveryLabel}>
      {children}
    </ErrorBoundary>
  );
}

export default ErrorBoundary;
